/**
 * Level Up scoring — the TS twin of `public.level_up_score_of` (docs/LEVEL_UP.md
 * "Rules → Scoring"; migration 20261013120000_level_up.sql).
 *
 * ONE formula, written identically here and in SQL, integer arithmetic only:
 *
 *   max_points     = Σ points of every tier of every metric (tier 0 included —
 *                    it is worth 0, but its metric is still in the possible total)
 *   answered_count = metrics whose answer is not null (tier 0 counts as answered)
 *   metric_count   = number of metrics
 *   total_points   = Σ over answered metrics of the points of tiers 1..selected
 *                    (cumulative; tier 0 earns 0)
 *   pct_points     = max = 0 ? 0 : floor((200·total + max) / (2·max))   — round half UP
 *   completion_pct = count = 0 ? 0 : floor((20000·answered + count) / (2·count)) / 100
 *   badge_level    = the number of bands b with pct_points ≥ b (0 = Novice, 1–5 = stars)
 *
 * The shared oracle is `./scoring.vectors.json`; `supabase/tests/manual/level_up.sql`
 * embeds the same rows and checks the SQL function against them. Change both.
 */
/** Maalka's code badge bands (percent of possible points), Novice below the first. */
export const DEFAULT_BADGE_BANDS = Object.freeze([17.68, 34.24, 50.68, 67.12, 83.56]);
/** The number of bands `pctPoints` reaches (a band equal to it counts). */
export function badgeLevel(pctPoints, bands) {
    const list = bands ?? DEFAULT_BADGE_BANDS;
    let level = 0;
    for (const b of list)
        if (pctPoints >= b)
            level += 1;
    return level;
}
/**
 * Score one scorecard.
 *
 * @param tierPoints `{ metricId: [points of tier 0, 1, …, N] }` — every metric of the version.
 * @param answers    `{ metricId: selected tier | null }` — a missing key or `null` is unanswered;
 *                   a key for a metric not in `tierPoints` is ignored.
 * @param bands      the version's badge bands; `null`/omitted means {@link DEFAULT_BADGE_BANDS}.
 */
export function scoreScorecard(tierPoints, answers, bands) {
    let maxPoints = 0;
    let totalPoints = 0;
    let answeredCount = 0;
    let metricCount = 0;
    for (const [metricId, points] of Object.entries(tierPoints)) {
        metricCount += 1;
        for (const p of points)
            maxPoints += p;
        const selected = answers[metricId];
        if (selected === null || selected === undefined)
            continue;
        answeredCount += 1;
        // Cumulative: tiers 1..selected (tier 0 is index 0 and earns nothing).
        for (let level = 1; level <= selected && level < points.length; level += 1)
            totalPoints += points[level];
    }
    const pctPoints = maxPoints === 0 ? 0 : Math.floor((200 * totalPoints + maxPoints) / (2 * maxPoints));
    const completionPct = metricCount === 0 ? 0 : Math.floor((20000 * answeredCount + metricCount) / (2 * metricCount)) / 100;
    return {
        totalPoints,
        maxPoints,
        answeredCount,
        metricCount,
        pctPoints,
        completionPct,
        badgeLevel: badgeLevel(pctPoints, bands),
    };
}
//# sourceMappingURL=scoring.js.map